import React, { useState, useEffect } from 'react';
import CompMap from './CompMap';
import { proxyPhoto } from '../utils/photo';

// Clickable comparable-sale detail. Reused by both the demo's canned comps and live mls_sold_comps
// (identical shape), so a comp row anywhere can open the same specs+photo+map view.
//
// Photos load LAZILY and only here: demo comps carry a ready photo_url, but live comps don't —
// MLS Grid photo URLs expire (~hourly), so storing them at sync is useless. Instead we re-query
// MLS Grid for THIS comp's current signed URLs the moment the user opens the detail, via
// GET /api/mls-opportunities/comp-photos. So a comp's photos only ever cache when someone inspects
// it — a handful per session, not every comp on every underwrite.
function fmtK(n) { return n ? '$' + Math.round(n / 1000) + 'k' : '—'; }
function fmtDate(s) { if (!s) return '—'; const d = new Date(s); return isNaN(d) ? s : d.toLocaleDateString(); }

export default function CompDetailModal({ comp: c, subject, onClose }) {
  // Demo comps arrive with a photo_url already; live comps get fetched on open.
  const [photos, setPhotos] = useState(c?.photo_url ? [c.photo_url] : []);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Demo comps carry photo_url (skip). Live comps re-query MLS Grid by ListingId (= mls_id).
    if (!c || c.photo_url || !c.mls_id) return;
    let active = true;
    setLoading(true);
    fetch(`/api/mls-opportunities/comp-photos?listing_id=${encodeURIComponent(c.mls_id)}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (active) setPhotos(Array.isArray(d.photos) ? d.photos : []); })
      .catch(() => { if (active) setPhotos([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [c]);

  if (!c) return null;
  const tierColor = c.tier === 1 ? '#4A7C59' : '#E0A526';
  const hasPhotos = photos.length > 0;
  const cur = Math.min(idx, Math.max(0, photos.length - 1));
  const prev = (e) => { e?.stopPropagation(); setIdx(i => (i <= 0 ? photos.length - 1 : i - 1)); };
  const next = (e) => { e?.stopPropagation(); setIdx(i => (i >= photos.length - 1 ? 0 : i + 1)); };

  const specs = [
    c.beds && c.baths ? `${c.beds} bd / ${c.baths} ba` : null,
    c.sqft ? `${c.sqft.toLocaleString()} sqft` : null,
    c.year_built ? `Built ${c.year_built}` : null,
    c.lot_size_sqft ? `${c.lot_size_sqft.toLocaleString()} sqft lot` : null,
  ].filter(Boolean);

  const row = (label, value) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '6px 0', borderBottom: '1px solid rgba(232,227,218,0.5)' }}>
      <span style={{ color: 'var(--gray-500)' }}>{label}</span>
      <span style={{ color: 'var(--gray-300)', fontWeight: 600 }}>{value}</span>
    </div>
  );

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(20,18,15,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--navy)', border: '1px solid rgba(232,227,218,0.15)', borderRadius: 14, width: 'min(560px, 95vw)', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.4)' }}
      >
        {/* Photo header — single image, or a mini-carousel when the comp has several */}
        <div style={{ position: 'relative', height: 200, background: '#15130f', borderRadius: '14px 14px 0 0', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--gray-500)', fontSize: 12 }}>Loading photos…</div>
          ) : hasPhotos ? (
            <img src={proxyPhoto(photos[cur])} alt={c.address} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--gray-500)', fontSize: 12 }}>No photo available</div>
          )}

          {!loading && photos.length > 1 && (
            <>
              <button onClick={prev} style={navBtn('left')}>‹</button>
              <button onClick={next} style={navBtn('right')}>›</button>
              <div style={{ position: 'absolute', bottom: 8, right: 10, padding: '2px 8px', borderRadius: 6, background: 'rgba(26,23,20,0.7)', color: '#FAF7F2', fontSize: 10, fontWeight: 600 }}>
                {cur + 1} / {photos.length}
              </div>
            </>
          )}

          <button
            onClick={onClose}
            style={{ position: 'absolute', top: 10, right: 10, width: 30, height: 30, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.55)', color: '#fff', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
          >×</button>
          {c.tier && (
            <span style={{ position: 'absolute', top: 12, left: 12, fontSize: 10, padding: '3px 9px', borderRadius: 6, background: tierColor, color: '#fff', fontWeight: 700, letterSpacing: '0.04em' }}>
              TIER {c.tier} COMP
            </span>
          )}
        </div>

        <div style={{ padding: 18 }}>
          <h3 style={{ margin: '0 0 2px', fontSize: 17, fontWeight: 700, color: 'var(--white)' }}>{c.address}</h3>
          <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 10 }}>
            {[c.city, c.zip].filter(Boolean).join(', ')}{specs.length ? ' · ' + specs.join(' · ') : ''}
          </div>

          <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--white)' }}>{fmtK(c.sold_price)}</div>
              <div style={{ fontSize: 10, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sold price</div>
            </div>
            {c.price_per_sqft && (
              <div>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--teal)' }}>${c.price_per_sqft}</div>
                <div style={{ fontSize: 10, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>$/sqft</div>
              </div>
            )}
          </div>

          <div style={{ marginBottom: 14 }}>
            {row('Closed', fmtDate(c.closed_date || c.close_date))}
            {c.distance_mi != null && row('Distance from subject', `${c.distance_mi} mi`)}
            {c.days_ago != null && row('Sold', `${c.days_ago} days ago`)}
            {c.beds != null && row('Beds / Baths', `${c.beds} / ${c.baths}`)}
            {c.sqft != null && row('Living area', `${c.sqft.toLocaleString()} sqft`)}
            {c.lot_size_sqft != null && row('Lot size', `${Number(c.lot_size_sqft).toLocaleString()} sqft`)}
            {c.year_built != null && row('Year built', c.year_built)}
          </div>

          {/* Reuse CompMap to place subject + this comp */}
          {subject?.lat && subject?.lng && c.lat && c.lng && (
            <div style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid rgba(232,227,218,0.12)' }}>
              <CompMap subject={subject} comps={[c]} height={200} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function navBtn(side) {
  return {
    position: 'absolute', top: '50%', [side]: 8, transform: 'translateY(-50%)',
    width: 30, height: 30, borderRadius: '50%', border: 'none',
    background: 'rgba(0,0,0,0.5)', color: '#fff', cursor: 'pointer', fontSize: 18, lineHeight: 1,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
}
